/* eslint-disable import/prefer-default-export */
/* eslint-disable max-len */
import styled, { css } from 'styled-components';
import { DayPicker } from 'react-day-picker';

const DEFAULT_FONT_SIZE = '16px';
const DEFAULT_PADDING = '5px';
const DEFAULT_INACTIVE_COLOR = '#dce0e0';

// If highlightColor is transparent then apply default color for highlighted days.
export const StyledDatePicker = styled(DayPicker)`${
  (props) => {
    if (!props.styled) {
      return '';
    }

    const styles = props.styled;
    const { customStyle } = props;
    const highlightedColor = styles.selector?.active.color;
    const hbgc = styles.selector?.hover.backgroundColor;
    const abgc = styles.selector?.active.backgroundColor;
    const ic = styles.inactive ? styles.inactive.color : null;
    const hfc = styles.selector?.hover.color;
    const fs = styles ? styles.fontSize : DEFAULT_FONT_SIZE;
    const fontSize = customStyle ? customStyle.fontSize : fs;
    const padding = customStyle ? customStyle.padding : DEFAULT_PADDING;
    const inactiveColor = ic || DEFAULT_INACTIVE_COLOR;

    return css`
      && {
        width: 100%;
        margin: 0;
        --rdp-cell-size: 30px;
        --rdp-caption-font-size: ${fontSize};
        --rdp-outline: 1px solid ${hbgc};
        --rdp-accent-color: ${abgc};

        .rdp-table {
          max-width: 100%;
          width: 100%;
        }

        .rdp-month {
          width: 100%;
          margin: 0;
        }

        .rdp-day {
          overflow: unset;
        }

        .rdp-day_disabled.rdp-day_fakeDisabled {
          color: rgba(72, 99, 150, 0.341);
        }

        .rdp-day_outside {
          color: unset;
        }

        .rdp-day_selected {
          color: ${highlightedColor};
          background-color: ${abgc};
        }

        .rdp-day_bookedDates {
          cursor: default;

          &:hover {
            background-color: inherit;
          }
        }

        &.rdp:not(.rdp_interactionDisabled)
          .rdp-day:not(.rdp-day_bookedDates):not(.rdp-day_disabled):not(.rdp-day_selected):not(.rdp-day_outside):hover {
          background-color: ${hbgc};
          color: ${hfc};
        }

        .rdp-day_disabled,
        .rdp-day.rdp-day_bookedDates:not(.rdp-day_outside):not(.rdp-day_disabled),
        .rdp-day.rdp-day_nonAvailable:not(.rdp-day_outside):not(.rdp-day_disabled) {
          color: ${inactiveColor}
        }
      
        .rdp-day_disabled.rdp-day_inPast {
          pointer-events: none;
        }

        .rdp-day_selected.rdp-day_range_start:not(.rdp-day_disabled)::after,
        .rdp-day.rdp-day_selected.rdp-day_range_start.rdp-day_outside::after,
        .rdp-day.rdp-day_selected.rdp-day_range_end.rdp-day_outside::after,
        .rdp-day_selected.rdp-day_range_end:not(.rdp-day_disabled)::after {
          content: "";
          position: absolute;
          width: 7px;
          height: 7px;
          border-radius: 2px;
          background-color: #ffffff;
        }

        .rdp-day.rdp-day_selected.rdp-day_range_start.rdp-day_outside::after,
        .rdp-day.rdp-day_selected.rdp-day_range_end.rdp-day_outside::after,
        .rdp-day_selected.rdp-day_range_end:not(.rdp-day_disabled)::after {
          bottom: 3px;
          right: 3px;
        }

        .rdp-day_selected.rdp-day_range_start:not(.rdp-day_disabled)::after {
          top: 3px;
          left: 3px;
        }

        .rdp-day.rdp-day_selected.rdp-day_outside {
          color: #ffffff;
        }

        .rdp-day.rdp-day_selected.rdp-day_range_start.rdp-day_outside,
        .rdp-day.rdp-day_selected.rdp-day_range_end.rdp-day_outside {
          position: relative;
        }

        .rdp-day.rdp-day_blackoutOverlap:not(.rdp-day_outside):not(.rdp-day_disabled) {
          color: #d1d1d1;
        }

        .rdp-day.rdp-day_hoverHighlight:not(.rdp-day_selected) {
          color: ${highlightedColor};
          background-color: ${hbgc};
        }

        .rdp-caption_label {
          z-index: 0;
          padding: 0;
          font-weight: 400;
        }

        .rdp-nav {
          margin-right: 12px
        }

        .rdp-nav_button {
          border-radius: 4px;
          color: ${styles.color};

          &:not(:disabled):hover {
            color: ${highlightedColor};
            background-color: var(--rdp-accent-color);
          }
        }

        .rdp-nav_button_next {
          margin-left: 22px
        }

        .rdp-head {
          margin-top: 4px;
        }

        .rdp-head_row,
        .rdp-row {
          display: grid;
          grid-template-columns: repeat(7, 1fr);
          justify-items: center;
        }

        .rdp-head_cell {
          color: ${styles.color};
          padding: ${padding};
          font-size: ${fontSize};
          line-height: ${fontSize};
          font-weight: 400;
          height: auto;
          text-transform: capitalize;
        }

        .rdp-cell {
          width: 100%;
        }

        .rdp-cell .rdp-button[disabled]:not(.rdp-day_selected) {
          opacity: 1;
        }

        .rdp-day {
          position: relative;
          border-radius: 0;
          padding: ${padding};
          padding-bottom: 7px;
          font-size: ${fontSize};
          width: 100%;
          max-width: inherit;

          :not(.rdp-day_hoverHighlight):hover {
            border-radius: 5px;
          }
        }

        .rdp-day.rdp-day_hoverStart,
        .rdp-day.rdp-day_range_start {
          border-top-left-radius: 5px;
          border-bottom-left-radius: 5px;
        }

        .rdp-day.rdp-day_hoverEnd,
        .rdp-day.rdp-day_range_end {
          border-top-right-radius: 5px;
          border-bottom-right-radius: 5px;
        }

        .rdp-day.rdp-day_range_start.rdp-day.rdp-day_range_end.rdp-day_hoverHighlight {
          border-top-right-radius: 0;
          border-bottom-right-radius: 0;
        }

        .rdp-day.rdp-day_blackoutOverlap:hover::before,
        .rdp-day.rdp-day_bookedDates:hover::before,
        .rdp-day.rdp-day_disabled:not(.rdp-day_inPast):not(.rdp-day_bookedDates):not(.rdp-day_blackoutOverlap):hover::before {
          content: "";
          position: absolute;
          top: -6px;
          left: 50%;
          transform: translateX(-50%);
          border-width: 4px 6px 0 6px;
          border-style: solid;
          border-color: rgba(0,0,0,0.7) transparent transparent transparent;
          z-index: 100;
        }

        .rdp-day.rdp-day_blackoutOverlap:hover::after,
        .rdp-day.rdp-day_bookedDates:hover::after,
        .rdp-day.rdp-day_disabled:not(.rdp-day_inPast):not(.rdp-day_bookedDates):not(.rdp-day_blackoutOverlap):hover::after {
          position: absolute;
          left: 50%;
          top: -6px;
          transform: translateX(-50%) translateY(-100%);
          background-color: rgba(0,0,0,0.7);
          text-align: center;
          color: #ffffff;
          padding: 4px 2px;
          font-size: 12px;
          min-width: 80px;
          border-radius: 5px;
          pointer-events: none;
        }
        
        .rdp-day.rdp-day_blackoutOverlap:hover::after {
          content: '${props.tooltips ? props.tooltips.blackoutsOverlap : 'Blacked out'}';
        }

        .rdp-day.rdp-day_bookedDates:hover::after {
          content: '${props.tooltips ? props.tooltips.bookedDates : 'Cannot be chosen as start date'}';
        }

        .rdp-day.rdp-day_disabled:not(.rdp-day_inPast):not(.rdp-day_bookedDates):not(.rdp-day_blackoutOverlap):hover::after {
          content: '${props.tooltips ? props.tooltips.nonAvailable : 'Shop closed'}';
        }
      }`;
  }
}`;

export const StyledLegendBackground = styled.div`
  background-color: ${(props) => (props.backgroundColor ? props.backgroundColor : '#ffdbd3')};
  min-width: 36px;
  width: 36px;
  height: 24px;
  position: relative;
  border-top-left-radius: ${(props) => (props.start ? '5px' : '0')};
  border-bottom-left-radius: ${(props) => (props.start ? '5px' : '0')};
  border-top-right-radius: ${(props) => (props.end ? '5px' : '0')};
  border-bottom-right-radius: ${(props) => (props.end ? '5px' : '0')};
`;

export const StyledRectangleWhite = styled.div`
  position: absolute;
  width: 11px;
  height: 11px;
  background-color: white;
  top: ${(props) => (props.start ? '3px' : 'initial')};
  left: ${(props) => (props.start ? '3px' : 'initial')};
  right: ${(props) => (props.end ? '3px' : 'initial')};
  bottom: ${(props) => (props.end ? '3px' : 'initial')};
`;

export const StyledLegendWrapper = styled.div`
  display: flex;
  justify-content: space-between;
  margin-top: 16px;
`;

export const StyledLegendSection = styled.div`
  display: flex;
  align-items: end;
  max-width: 50%;
`;

export const StyledText = styled.p`
  display: flex;
  padding: 0 10px;
  margin: 0;
  flex-direction: column;
  word-break: break-word;
`;
